import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../common/Section';
import { FaQuoteLeft } from 'react-icons/fa';

const TestimonialsContainer = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  padding: 3rem 0;
`;

const TestimonialWrapper = styled.div`
  position: relative;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    min-height: 450px;
  }
`;

const TestimonialCard = styled(motion.div)`
  background-color: white;
  border-radius: var(--border-radius);
  padding: 3rem;
  box-shadow: var(--box-shadow);
  text-align: center;
  position: absolute;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const QuoteIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  margin-bottom: 2rem;
`;

const TestimonialText = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  color: var(--text-dark);
  font-style: italic;
  margin-bottom: 2rem;
  max-width: 800px;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ClientInfo = styled.div`
  margin-top: auto;
`;

const ClientName = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
`;

const ClientTitle = styled.p`
  color: var(--text-light);
  font-size: 1rem;
`;

const NavigationDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 0.75rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--primary-light)'};
  }
`;

const testimonialVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0
  })
};

const Testimonials = () => {
  const testimonials = [
    {
      quote: "Talgo's position limits monitoring solution has revolutionized how we monitor and comply with CFTC position limits requirements. The real-time US energy trading alerts and comprehensive regulatory reporting have given us confidence in our gas trading operations.",
      name: "Chief Risk Officer",
      title: "US Energy Trading Company"
    },
    {
      quote: "We've seen a 40% reduction in US energy trading reconciliation errors since implementing Talgo's energy trading data integration platform. The seamless connectivity between our US energy trading systems has streamlined our operations and improved CFTC compliance significantly.",
      name: "Head of Trading Operations",
      title: "US Power Trading Company"
    },
    {
      quote: "The US energy exchange connectivity service from Talgo gave us access to NYMEX and ICE markets we previously couldn't reach efficiently. Their technical team's expertise in US gas and energy markets compliance is unmatched, and their CFTC compliant implementation was flawless.",
      name: "VP of Technology",
      title: "US Energy Trading Partners"
    }
  ];

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  
  const nextTestimonial = () => {
    setDirection(1);
    setCurrent((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };
  
  const prevTestimonial = () => {
    setDirection(-1);
    setCurrent((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };
  
  const goToTestimonial = (index) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    
    return () => clearInterval(interval);
  }, [current]);

  return (
    <Section
      title="US Energy Trading Compliance Success Stories"
      subtitle="Hear from leading US energy and gas trading firms who trust Talgo for position limits monitoring and CFTC compliance solutions"
      titleProps={{ centered: true, withLine: true, spaced: true }}
      subtitleProps={{ centered: true, muted: true }}
    >
      <TestimonialsContainer>
        <TestimonialWrapper>
          <AnimatePresence initial={false} custom={direction}>
            <TestimonialCard
              key={current}
              custom={direction}
              variants={testimonialVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'tween', duration: 0.5 }}
            >
              <QuoteIcon>
                <FaQuoteLeft />
              </QuoteIcon>
              <TestimonialText>
                {testimonials[current].quote}
              </TestimonialText>
              <ClientInfo>
                <ClientName>{testimonials[current].name}</ClientName>
                <ClientTitle>{testimonials[current].title}</ClientTitle>
              </ClientInfo>
            </TestimonialCard>
          </AnimatePresence>
        </TestimonialWrapper>
        
        <NavigationDots>
          {testimonials.map((_, index) => (
            <Dot 
              key={index} 
              active={current === index} 
              onClick={() => goToTestimonial(index)}
            />
          ))}
        </NavigationDots>
      </TestimonialsContainer>
    </Section>
  );
};

export default Testimonials; 